import { useContextProvider } from "./../context/providers/ContextProvider";
import { keepPreviousData, useMutation, useQueryClient } from "@tanstack/react-query";
import { useQuery } from "../_app/lib/query";
import { createRole, deleteRole, getAllRoles, getRoleById, updateRole } from "./api";
import { Role } from "./types";
import { useFeedbackAlerts } from "../_app/hooks";

export function useAllRoles(page?: number, limit?: number, options = {}) {
  const { setFeedbackAlertError } = useFeedbackAlerts();
  const { contextId: accountId } = useContextProvider();
  return useQuery({
    queryKey: ["roles", { accountId, page, limit }],
    queryFn: () => getAllRoles(accountId, page, limit),
    placeholderData: keepPreviousData,
    ...options,

    onError: (error: any) => {
      setFeedbackAlertError(error?.data?.message || "Failed to fetch roles");
    },
  });
}

export function useSingleRole(roleId: string, options = {}) {
  const { setFeedbackAlertError } = useFeedbackAlerts();
  const { contextId: accountId } = useContextProvider();
  return useQuery({
    queryKey: ["role", { accountId, roleId }],
    queryFn: () => getRoleById(accountId, roleId),
    ...options,

    onError: (error: any) => {
      setFeedbackAlertError(error?.data?.message || "Failed to fetch role");
    },
  });
}

export function useRoleUpdate(options: any = {}) {
  const queryClient = useQueryClient();
  const { setFeedbackAlertError, setFeedbackAlertSuccess } = useFeedbackAlerts();
  const { contextId: accountId } = useContextProvider();
  return useMutation({
    mutationFn: (role: Role) => updateRole(accountId, role),
    ...options,

    onSuccess: (role: any) => {
      queryClient.setQueryData(["role", { accountId, roleId: role.id }], () => role);
      queryClient.invalidateQueries({
        queryKey: ["roles"],
      });
      queryClient.invalidateQueries({
        queryKey: ["permissions"],
      });
      setFeedbackAlertSuccess("Role updated");
      options?.onSuccess?.();
    },

    onError: (error: any) => {
      setFeedbackAlertError(error?.data?.message || "Failed to update role");
    },
  });
}

export function useRoleCreate(options: any = {}) {
  const queryClient = useQueryClient();
  const { setFeedbackAlertError, setFeedbackAlertSuccess } = useFeedbackAlerts();
  const { contextId: accountId } = useContextProvider();
  return useMutation({
    mutationFn: (role: Role) => createRole(accountId, role),
    ...options,

    onSuccess: (role: any) => {
      queryClient.setQueryData(["role", { accountId, roleId: role.id }], () => role);
      queryClient.invalidateQueries({
        queryKey: ["roles"],
      });
      setFeedbackAlertSuccess("Role created");
      options?.onSuccess?.();
    },

    onError: (error: any) => {
      setFeedbackAlertError(error?.data?.message || "Failed to create role");
    },
  });
}

export function useRoleDelete(options = {}) {
  const queryClient = useQueryClient();
  const { setFeedbackAlertError, setFeedbackAlertSuccess } = useFeedbackAlerts();
  const { contextId: accountId } = useContextProvider();
  return useMutation({
    mutationFn: (user: Role) => deleteRole(accountId, user),
    ...options,

    onSuccess: (role: any) => {
      queryClient.invalidateQueries({
        queryKey: ["role", { accountId, roleId: role.id }],
      });
      queryClient.invalidateQueries({
        queryKey: ["roles"],
      });
      setFeedbackAlertSuccess("Role deleted");
    },

    onError: (error: any) => {
      setFeedbackAlertError(error?.data?.message || "Failed to delete role");
    },
  });
}
